'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherKey, FeatherRefreshCw } from '@subframe/core';
import { useState } from 'react';
import { Alert, Button, Checkbox, Dialog, TextField } from '@/shared/components/ui';
import { DIAS_DE_SOBREVIDA, OPCOES_DE_ESCOPO, TAMANHO_MAXIMO_DO_NOME } from '../constants/chavesDeApi';
import type { EnvioDoFormulario, EscopoConcedivel, ErroDasChaves, ModoDoFormulario } from '../types/chavesDeApiTypes';
import { fimDaSobrevidaSeRotacionarAgora, problemaDoFormulario, rotuloDaChave } from '../utils/regrasDaTela';
interface FormularioDeChaveModalProps {
  modo: ModoDoFormulario;
  enviando: boolean;
  erro: ErroDasChaves | null;
  onFechar: () => void;
  onEnviar: (envio: EnvioDoFormulario) => void;
}

/**
 * Criar pede nome e escopos, nenhum marcado (D10-10). Rotacionar pede só o nome: a chave nova herda
 * os acessos da antiga no core (R4.1, decidido em 30/09), então a tela não oferece escopos.
 * O componente é montado a cada abertura (`key` no pai), então o formulário sempre nasce vazio.
 */
export function FormularioDeChaveModal(t0) {
  const $ = _c(73);
  const {
    modo,
    enviando,
    erro,
    onFechar,
    onEnviar
  } = t0;
  const [nome, setNome] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [escopos, setEscopos] = useState(t1);
  const [tentouEnviar, setTentouEnviar] = useState(false);
  const rotacao = modo.tipo === "rotacionar";
  let t2;
  if ($[1] !== escopos || $[2] !== nome || $[3] !== rotacao) {
    t2 = problemaDoFormulario(rotacao ? {
      nome
    } : {
      nome,
      escopos
    });
    $[1] = escopos;
    $[2] = nome;
    $[3] = rotacao;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const problema = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (escopo, marcado) => {
      setEscopos(atuais => marcado ? [...atuais, escopo] : atuais.filter(atual => atual !== escopo));
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const alternarEscopo = t3;
  let t4;
  if ($[6] !== enviando || $[7] !== escopos || $[8] !== modo.chave || $[9] !== modo.tipo || $[10] !== nome || $[11] !== onEnviar || $[12] !== problema) {
    t4 = () => {
      setTentouEnviar(true);
      if (problema || enviando) {
        return;
      }
      onEnviar(modo.tipo === "rotacionar" ? {
        tipo: "rotacionar",
        chave: modo.chave,
        dados: {
          nome: nome.trim()
        }
      } : {
        tipo: "criar",
        dados: {
          nome: nome.trim(),
          escopos
        }
      });
    };
    $[6] = enviando;
    $[7] = escopos;
    $[8] = modo.chave;
    $[9] = modo.tipo;
    $[10] = nome;
    $[11] = onEnviar;
    $[12] = problema;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const enviar = t4;
  const erroDoNome = tentouEnviar && problema === "NOME_VAZIO" ? "D\xEA um nome \xE0 chave para reconhec\xEA-la depois." : problema === "NOME_LONGO" ? `Use no máximo ${TAMANHO_MAXIMO_DO_NOME} caracteres.` : null;
  let t5;
  if ($[14] !== enviando || $[15] !== onFechar) {
    t5 = aberto => {
      if (!aberto && !enviando) {
        onFechar();
      }
    };
    $[14] = enviando;
    $[15] = onFechar;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== rotacao) {
    t6 = <div className="bg-brand-50 text-brand-600 flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]">{rotacao ? <FeatherRefreshCw /> : <FeatherKey />}</div>;
    $[17] = rotacao;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const t7 = rotacao ? "Rotacionar chave" : "Criar chave de API";
  let t8;
  if ($[19] !== t7) {
    t8 = <h2 className="text-default-font text-lg font-semibold">{t7}</h2>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== modo.chave || $[22] !== rotacao) {
    t9 = rotacao ? `Uma chave nova substitui ${rotuloDaChave(modo.chave)}.` : "A chave d\xE1 ao sistema do escrit\xF3rio acesso \xE0 API da Arbitralis.";
    $[21] = modo.chave;
    $[22] = rotacao;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9) {
    t10 = <p className="text-subtext-color mt-0.5 text-sm">{t9}</p>;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t8) {
    t11 = <div className="min-w-0 flex-1">{t8}{t10}</div>;
    $[26] = t10;
    $[27] = t8;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t11 || $[30] !== t6) {
    t12 = <div className="flex w-full items-start gap-3 border-b border-neutral-100 px-6 py-5">{t6}{t11}</div>;
    $[29] = t11;
    $[30] = t6;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== rotacao) {
    t13 = rotacao && <Alert variant="warning" title={`A chave atual continua valendo por ${DIAS_DE_SOBREVIDA} dias`} description={`Ela deixa de funcionar em ${fimDaSobrevidaSeRotacionarAgora()}. Troque a chave no sistema do escritório antes dessa data.`} />;
    $[32] = rotacao;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== rotacao) {
    t14 = rotacao && <Alert variant="neutral" title={"A chave nova mant\xE9m os mesmos acessos da atual"} description={"Para mudar o que a chave pode acessar, \xE9 preciso revog\xE1-la e criar outra; a integra\xE7\xE3o para nesse intervalo."} />;
    $[34] = rotacao;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== erro) {
    t15 = erro && <Alert variant="error" title={"N\xE3o foi poss\xEDvel concluir"} description={erro.mensagem} />;
    $[36] = erro;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const t16 = erroDoNome ?? "Ex.: Integra\xE7\xE3o do ERP. Aparece s\xF3 nesta tela.";
  const t17 = erroDoNome !== null;
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = evento => setNome(evento.target.value);
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== nome) {
    t19 = <TextField.Input value={nome} maxLength={TAMANHO_MAXIMO_DO_NOME + 20} onChange={t18} aria-label="Nome da chave" />;
    $[39] = nome;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t16 || $[42] !== t17 || $[43] !== t19) {
    t20 = <TextField label="Nome da chave" helpText={t16} error={t17} className="w-full">{t19}</TextField>;
    $[41] = t16;
    $[42] = t17;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== escopos || $[46] !== problema || $[47] !== rotacao || $[48] !== tentouEnviar) {
    t21 = !rotacao && <fieldset className="flex w-full flex-col gap-3"><legend className="text-default-font mb-1 text-sm font-semibold">O que a chave pode acessar</legend>{OPCOES_DE_ESCOPO.map(opcao => <div key={opcao.escopo} className="flex flex-col gap-0.5"><Checkbox label={opcao.rotulo} checked={escopos.includes(opcao.escopo)} onCheckedChange={marcado_0 => alternarEscopo(opcao.escopo, marcado_0)} /><span className="text-subtext-color pl-6 text-xs">{opcao.descricao}</span></div>)}{tentouEnviar && problema === "SEM_ESCOPO" && <span className="text-error-700 text-xs">Marque ao menos um acesso.</span>}</fieldset>;
    $[45] = escopos;
    $[46] = problema;
    $[47] = rotacao;
    $[48] = tentouEnviar;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t13 || $[51] !== t14 || $[52] !== t15 || $[53] !== t20 || $[54] !== t21) {
    t22 = <div className="flex w-full flex-col gap-5 px-6 py-5">{t13}{t14}{t15}{t20}{t21}</div>;
    $[50] = t13;
    $[51] = t14;
    $[52] = t15;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== enviando || $[57] !== onFechar) {
    t23 = <Button variant="neutral-secondary" onClick={onFechar} disabled={enviando}>Cancelar</Button>;
    $[56] = enviando;
    $[57] = onFechar;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  const t24 = rotacao ? "Rotacionar" : "Criar chave";
  let t25;
  if ($[59] !== enviando || $[60] !== enviar || $[61] !== t24) {
    t25 = <Button variant="brand-primary" onClick={enviar} loading={enviando}>{t24}</Button>;
    $[59] = enviando;
    $[60] = enviar;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== t23 || $[64] !== t25) {
    t26 = <div className="flex w-full items-center justify-end gap-3 border-t border-neutral-100 px-6 py-4">{t23}{t25}</div>;
    $[63] = t23;
    $[64] = t25;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== t12 || $[67] !== t22 || $[68] !== t26) {
    t27 = <Dialog.Content className="w-full max-w-[560px] gap-0 rounded-xl p-0">{t12}{t22}{t26}</Dialog.Content>;
    $[66] = t12;
    $[67] = t22;
    $[68] = t26;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== t27 || $[71] !== t5) {
    t28 = <Dialog open={true} onOpenChange={t5}>{t27}</Dialog>;
    $[70] = t27;
    $[71] = t5;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  return t28;
}